import {
  addressResponseType,
  getNearestAsset,
  isCoordinatesInsideBorough,
} from '@utils';
import 'leaflet/dist/leaflet.css';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { scrollTop } from '@utils';
import { RoutesList } from '../routes/Routes';
import { AssetType, ErrorMessages } from '@types';
import { MapTemplate } from '@templates';
import { useHighwaysStore } from '@highways/store/store';
import {
  getAddressErrorMessage,
  convertAddressToServiceLocation,
  convertServiceLocationToAddress,
  formatFullAddress,
} from '@highways/utils/formatServiceLocation';
import { LatLngTuple } from 'leaflet';

const SelectedLocationTextContent = ({
  fullAddress,
}: {
  fullAddress: string;
}) => {
  const { highwaysType } = useHighwaysStore();
  return (
    <div className="text-bodyXl mt-4 flex flex-col gap-[1.25rem]">
      <p className="font-semibold text-black">
        {`Location of the ${highwaysType} damage`}
      </p>
      <p className="font-normal text-black">{fullAddress}</p>
    </div>
  );
};

export const WhereIsTheRoadDamageLocatedPage = () => {
  const navigate = useNavigate();
  const {
    highwaysType,
    updateServiceLocationDetails,
    serviceLocationDetails,
    serviceAssetDetails,
    updateServiceAssetDetails,
    isPhotoLocationStored,
    updateIsPhotoLocationStored,
  } = useHighwaysStore();

  const initialAddress: addressResponseType =
    serviceLocationDetails.latitude && serviceLocationDetails.longitude
      ? convertServiceLocationToAddress(
          serviceLocationDetails,
          serviceAssetDetails,
        )
      : ({} as addressResponseType);
  const [address, setAddress] = useState<addressResponseType>(initialAddress);
  const defaultLocation =
    address.latitude && address.longitude
      ? ([
          parseFloat(address.latitude),
          parseFloat(address.longitude),
        ] as LatLngTuple)
      : undefined;
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const errorPath = 'damageLocation';
  const labelText = isPhotoLocationStored
    ? `Check if the location is correct`
    : `Where is the ${highwaysType} damage located?`;
  const assetType: AssetType =
    highwaysType === 'road' ? 'HighwaysCarriageways' : 'HighwaysFootways';

  // redirect to the landing page if the highwaysType is not set
  useEffect(() => {
    if (!highwaysType) {
      navigate(RoutesList.TEST_LANDING_PAGE);
    }
  }, []);

  const subText = isPhotoLocationStored ? (
    <div className="text-bodyLg flex flex-col gap-4">
      <p>
        We used your photo to help figure out the location of the road damage.
      </p>
      <p>You can change the location of the issue using the map.</p>
    </div>
  ) : undefined;

  const handleOnNextClick = async () => {
    if (address.latitude && address.longitude) {
      const asset = await getNearestAsset(
        Number.parseFloat(address.latitude),
        Number.parseFloat(address.longitude),
        assetType,
      );
      if (asset?.within_tfl_boundary) {
        navigate(RoutesList.TFL_REPORT);
        scrollTop();
        return;
      }
    }

    const errorMessage = getAddressErrorMessage(address, highwaysType);
    if (errorMessage) {
      setErrorMessages([
        {
          message: errorMessage,
          path: errorPath,
        },
      ]);
      return;
    }

    updateServiceLocationDetails(convertAddressToServiceLocation(address));
    if (address.asset) {
      updateServiceAssetDetails(address.asset);
    }
    navigate(RoutesList.ANYTHING_ELSE);
    scrollTop();
  };

  const buttonLeft = {
    label: 'Previous',
    onClick: () => {
      navigate(RoutesList.UPLOAD_PHOTO);
      scrollTop();
    },
  };
  const buttonRight = {
    label: 'Next',
    onClick: handleOnNextClick,
  };

  const handleAddressChange = (address: addressResponseType) => {
    setAddress(address);
    setErrorMessages([]);
  };
  const { longitude, latitude } = serviceLocationDetails;
  useEffect(() => {
    const checkCoordinates = async () => {
      if (isPhotoLocationStored && longitude && latitude) {
        const isInside = await isCoordinatesInsideBorough([
          latitude,
          longitude,
        ]);
        if (!isInside) {
          // reverts to the normal map page and clears the outside borough coordinates stored
          updateIsPhotoLocationStored(false);
          updateServiceLocationDetails({
            ...serviceLocationDetails,
            latitude: null,
            longitude: null,
          });
        }
      }
    };

    checkCoordinates();
  }, []);

  const selectedAddressContent = (
    <SelectedLocationTextContent fullAddress={address.full_address} />
  );

  return (
    <MapTemplate
      id={errorPath}
      labelText={labelText}
      subText={subText}
      errorPath={errorPath}
      errorMessages={errorMessages}
      onChangeAddress={handleAddressChange}
      defaultLocation={defaultLocation}
      selectedAddress={address}
      customAddressContent={selectedAddressContent}
      assetType={assetType}
      buttonNext={buttonRight}
      buttonPrevious={buttonLeft}
    />
  );
};
